import React, { useState, useEffect, useCallback, useRef } from "react";
import { Menu, X, Phone, ChevronDown, ExternalLink, Camera, Images, UserCheck } from "lucide-react";
import { useLocation } from "react-router-dom";
import SmartLink from "@/components/SmartLink";
import { SITE } from "@/data/site";
import ThemeToggle from "@/components/ThemeToggle";
import { useBrand } from "@/lib/brand";

const LEADS_URL = "https://visite-virtuelle.virtualpeek.fr";

const SERVICES_MENU = [
  {
    label: "Visites virtuelles 360°",
    desc: "Captation Matterport Pro3, visite hébergée et prête à diffuser.",
    href: "/#services",
    icon: Camera,
  },
  {
    label: "Photos haute qualité",
    desc: "Visuels 4K extraits de la visite, prêts pour vos annonces.",
    href: "https://virtualpeek.pixieset.com/",
    icon: Images,
    external: true,
  },
  {
    label: "Qualification de leads",
    desc: "Chaque visite devient un prospect qualifié — site dédié.",
    href: LEADS_URL,
    icon: UserCheck,
    external: true,
  },
];

const NAV = [
  { label: "Services", submenu: SERVICES_MENU },
  { label: "Réalisations", href: "/nos-realisations" },
  { label: "Tarifs", href: "/nos-tarifs" },
  { label: "Conseils", href: "/virtualtips" },
  { label: "Contact", href: "/#contact" },
  { label: "Annonces", href: "/annonces" },
];

function useActiveKey() {
  const { pathname } = useLocation();
  if (pathname.startsWith("/nos-realisations") || pathname.startsWith("/realisations")) return "Réalisations";
  if (pathname.startsWith("/nos-tarifs")) return "Tarifs";
  if (pathname.startsWith("/virtualtips")) return "Conseils";
  if (pathname.startsWith("/portfolio")) return "Services";
  if (pathname.startsWith("/annonces")) return "Annonces";
  return null;
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [submenu, setSubmenu] = useState(false);
  const [mobileServices, setMobileServices] = useState(false);
  const submenuRef = useRef(null);
  const closeTimer = useRef(null);
  const active = useActiveKey();
  const brand = useBrand();

  const onScroll = useCallback(() => setScrolled(window.scrollY > 20), []);

  useEffect(() => {
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [onScroll]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    if (!submenu) return;
    const onKey = (e) => e.key === "Escape" && setSubmenu(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [submenu]);

  const openSubmenu = () => {
    clearTimeout(closeTimer.current);
    setSubmenu(true);
  };
  const closeSubmenu = (delay = 140) => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setSubmenu(false), delay);
  };

  return (
    <>
      {open && (
        <div
          onClick={() => setOpen(false)}
          data-testid="mobile-menu-backdrop"
          className="lg:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm vp-fade-in"
        />
      )}

      <header
        data-testid="site-header"
        onClick={(e) => open && e.target === e.currentTarget && setOpen(false)}
        className={`fixed top-0 left-0 right-0 z-50 glass transition-[padding] duration-200 ${
          scrolled ? "py-2.5" : "py-3.5"
        }`}
      >
        <div className="vp-shell px-4 md:px-10 flex items-center justify-between gap-2">
          <SmartLink
            href="/"
            onClick={(e) => {
              if (window.location.pathname !== "/") return;
              e.preventDefault();
              window.history.replaceState(null, "", "/");
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            data-testid="logo-link"
            className="flex items-center group flex-shrink-0"
          >
            <img
              src={brand.logoFull}
              srcSet={brand.logoFullSrcSet}
              sizes="(min-width: 768px) 192px, 144px"
              width="192"
              height="44"
              fetchPriority="high"
              alt="VirtualPeek - Visites virtuelles 360 dans l'Hérault"
              className={`h-9 md:h-11 w-36 md:w-48 ${brand.logoClass} transition-transform group-hover:scale-[1.03]`}
              style={brand.logoStyle}
              data-testid="header-logo"
            />
          </SmartLink>

          <nav className="hidden lg:flex items-center gap-1" aria-label="Navigation principale">
            {NAV.map((n) =>
              n.submenu ? (
                <div
                  key={n.label}
                  className="relative"
                  ref={submenuRef}
                  onMouseEnter={openSubmenu}
                  onMouseLeave={() => closeSubmenu()}
                  onFocusCapture={openSubmenu}
                  onBlurCapture={() => closeSubmenu(60)}
                >
                  <button
                    type="button"
                    onClick={() => !submenu && setSubmenu(true)}
                    aria-expanded={submenu}
                    aria-haspopup="true"
                    data-active={active === n.label || undefined}
                    data-testid="nav-services"
                    className="nav-link gap-1.5"
                  >
                    {n.label}
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${submenu ? "rotate-180" : ""}`} />
                  </button>

                  {submenu && (
                      <div
                        className="nav-panel absolute left-0 top-full mt-3 w-[22rem] p-2 vp-menu-in"
                        data-testid="nav-services-panel"
                      >
                        {n.submenu.map((s) => {
                          const Icon = s.icon;
                          return (
                            <SmartLink
                              key={s.label}
                              href={s.href}
                              target={s.external ? "_blank" : undefined}
                              rel={s.external ? "noopener noreferrer" : undefined}
                              onClick={() => setSubmenu(false)}
                              data-testid={`nav-submenu-${s.label}`}
                              className="nav-panel-item group"
                            >
                              <span className="mt-0.5 w-9 h-9 rounded-lg bg-[#E50914]/12 border border-[#E50914]/25 flex items-center justify-center flex-shrink-0">
                                <Icon className="w-4 h-4 text-[#E50914]" />
                              </span>
                              <span className="min-w-0">
                                <span className="flex items-center gap-1.5 text-sm font-medium text-[var(--vp-text)]">
                                  {s.label}
                                  {s.external && <ExternalLink className="w-3 h-3 text-zinc-500" />}
                                </span>
                                <span className="block text-xs text-[var(--vp-text-muted)] leading-snug mt-0.5">{s.desc}</span>
                              </span>
                            </SmartLink>
                          );
                        })}
                      </div>
                    )}
                </div>
              ) : (
                <SmartLink
                  key={n.label}
                  href={n.href}
                  data-active={active === n.label || undefined}
                  data-testid={`nav-${n.label}`}
                  className="nav-link"
                >
                  {n.label}
                </SmartLink>
              )
            )}
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            <a
              href={`tel:${SITE.phoneRaw}`}
              data-testid="header-phone"
              className="hidden 2xl:inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors whitespace-nowrap"
            >
              <Phone className="w-4 h-4 text-[#E50914]" /> {SITE.phone}
            </a>
            <ThemeToggle />
            <SmartLink
              href="/#contact"
              data-testid="header-cta"
              className="btn-primary hidden md:inline-flex whitespace-nowrap !px-5 !py-2.5 text-[0.85rem]"
            >
              Demander un devis
            </SmartLink>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((o) => !o)}
              className="lg:hidden w-11 h-11 -mr-2 flex items-center justify-center text-[var(--vp-text)]"
              aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
              aria-expanded={open}
            >
              {open ? <X /> : <Menu />}
            </button>
          </div>
        </div>

        {open && (
            <div
              className="lg:hidden nav-panel mx-4 mt-3 p-3 max-h-[calc(100dvh-110px)] overflow-y-auto vp-menu-in"
              data-testid="mobile-menu"
            >
              <ul className="space-y-1">
                {NAV.map((n, i) => (
                  <li
                    key={n.label}
                    className="vp-fade-in"
                    style={{ animationDelay: `${i * 35}ms` }}
                  >
                    {n.submenu ? (
                      <>
                        <button
                          type="button"
                          onClick={() => setMobileServices((v) => !v)}
                          aria-expanded={mobileServices}
                          data-testid="mobile-nav-services"
                          className="w-full flex items-center justify-between min-h-[48px] px-3 rounded-xl text-[var(--vp-text)] hover:bg-white/5 transition"
                        >
                          {n.label}
                          <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${mobileServices ? "rotate-180" : ""}`} />
                        </button>
                        {mobileServices && (
                            <div className="overflow-hidden pl-2 vp-fade-in">
                              {n.submenu.map((s) => (
                                <SmartLink
                                  key={s.label}
                                  href={s.href}
                                  target={s.external ? "_blank" : undefined}
                                  rel={s.external ? "noopener noreferrer" : undefined}
                                  onClick={() => setOpen(false)}
                                  data-testid={`mobile-nav-${s.label}`}
                                  className="flex items-center gap-2 min-h-[48px] px-3 text-sm text-[var(--vp-text-muted)] hover:text-[#E50914] transition-colors"
                                >
                                  {s.label}
                                  {s.external && <ExternalLink className="w-3 h-3" />}
                                </SmartLink>
                              ))}
                            </div>
                          )}
                      </>
                    ) : (
                      <SmartLink
                        href={n.href}
                        onClick={() => setOpen(false)}
                        data-active={active === n.label || undefined}
                        data-testid={`mobile-nav-${n.label}`}
                        className="flex items-center min-h-[48px] px-3 rounded-xl text-[var(--vp-text)] hover:bg-white/5 transition"
                      >
                        {n.label}
                      </SmartLink>
                    )}
                  </li>
                ))}
              </ul>

              <div className="pt-3">
                <ThemeToggle variant="full" idPrefix="mobile-" />
              </div>

              <div className="grid grid-cols-2 gap-3 pt-3">
                <a
                  href={`tel:${SITE.phoneRaw}`}
                  onClick={() => setOpen(false)}
                  data-testid="mobile-menu-call"
                  className="btn-secondary justify-center"
                >
                  <Phone className="w-4 h-4 text-[#E50914]" /> Appeler
                </a>
                <SmartLink
                  href="/#contact"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-menu-quote"
                  className="btn-primary justify-center"
                >
                  Devis
                </SmartLink>
              </div>
            </div>
          )}
      </header>
    </>
  );
}
